<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import type { ChoiceDto, Locales, MapCategoryDto } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import { useI18n } from 'vue-i18n';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { useFormsStore } from '@/stores';
import ImageField from '@/components/ImageField.vue';
import { localizableString } from '@/helpers/validation.helper';
import { basemaps, mapMarkers } from '@/helpers/map-style';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import { provideEditLocale } from '@/composables/data-display-utils';

const { t } = useI18n();
const formsStore = useFormsStore();

// PROPS
const props = defineProps<{
  mode: 'add' | 'edit';
  category: MapCategoryDto;
  stepId: number;
  formId: number;
  languageEnabled: Locales[];
}>();

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// FORM
const { handleSubmit, errors, meta } = useForm({
  initialValues: props.category,
  validationSchema: yup.object<MapCategoryDto>({
    name: localizableString(props.languageEnabled),
    color: yup.string(),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const markerSelected = ref(mapMarkers[0]);
const dialogTitle = computed(() =>
  props.mode === 'add' ? t('form.dialogs.map-category-row.create') : t('form.dialogs.map-category-row.edit'),
);

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  await wrapSavingOperation(
    async () => await formsStore.saveMapCategory(values, props.formId, props.stepId, markerSelected),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="props.category" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-input
          name="name"
          label="Texte"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />

        <localized-select
          name="color"
          :label="$t('form.map-category.fields.marker-color')"
          :editLocale="editLocale"
          :options="mapMarkers"
          :option-html="true"
          emit-value
          map-options
          outlined
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat label="Cancel" @click="onDialogCancel" />
        <q-btn color="primary" label="Save" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
